import { IconButton, Stack, Text } from '@chakra-ui/react'
import {
  faEllipsis,
  faFlag,
  faPencil,
  faSmilePlus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { isNotNull } from '@/utils/helpers'

import { useUser } from '@/client/context/user'
import { useRelativeTimeShort } from '@/client/hooks'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'

import type { PostEventDetails } from '@/components/Community/Post/types'
import ReportModalV2 from '@/components/Report/ReportModalV2'
import Icon from '@/components/UI/Icon/Icon'
import Tippy from '@/components/UI/Tippy'
import ReactionButton from '@/components/Worlds/Reaction/ReactionButton'
import AvatarWithNameAndPopover from '@/components/Worlds/UI/Avatar/AvatarWithNameAndPopover'
import Menu from '@/components/Worlds/UI/Menu/Menu'
import type { MenuItemType } from '@/components/Worlds/UI/Menu/types'

export default function PostEventCommentHeader({
  postEvent,
  onEdit,
  onDelete,
  onToggleReaction,
  isOptimistic = false,
}: {
  postEvent: PostEventDetails
  onEdit: (p: PostEventDetails) => void
  onDelete: (p: PostEventDetails) => void
  onToggleReaction: (reaction: string) => Promise<void>
  isOptimistic?: boolean
}): JSX.Element {
  const currentUser = useUser()

  const [showReportModal, setShowReportModal] = useState(false)

  const { createdAt, user, updatedAt } = postEvent

  const createdAtRelative = useRelativeTimeShort({ time: createdAt })

  const canEditComment =
    currentUser.id === user.id || hasUserAuthScope(currentUser, 'admin:post-events:edit')

  const canDeleteComment =
    currentUser.id === user.id || hasUserAuthScope(currentUser, 'admin:post-events:delete')

  const menuItems: MenuItemType[] = [
    { icon: faFlag, label: 'Report Comment', onClick: () => setShowReportModal(true) },
    canEditComment
      ? { icon: faPencil, label: 'Edit Comment', onClick: () => onEdit(postEvent) }
      : null,
    canDeleteComment
      ? {
          icon: faTrash,
          color: 'error.fg',
          label: 'Delete Comment',
          onClick: () => onDelete(postEvent),
        }
      : null,
  ].filter(isNotNull)

  return (
    <>
      <ReportModalV2
        entity={{ type: 'post-comment', id: postEvent.id }}
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        reportedUserName={postEvent.user.displayName}
      />

      <Stack direction="row" align="center" justify="space-between">
        <Stack direction="row" align="center" spacing="12px">
          <AvatarWithNameAndPopover
            user={user}
            variant="icon"
            omitAnniversaryTooltip
            worldLevel={postEvent.worldInfo?.userWorldLevel}
            world={postEvent.worldInfo?.worldDetails}
          />
          <Tippy content={createdAt.toLocaleString(DateTime.DATETIME_FULL)}>
            <Text variant="supporting" color="fg.muted">
              {createdAtRelative}
            </Text>
          </Tippy>
          {updatedAt > createdAt ? (
            <Text variant="supporting" color="fg.muted">
              (edited)
            </Text>
          ) : null}
        </Stack>
        <Stack
          direction="row"
          spacing="4px"
          display="none"
          _groupHover={{ display: isOptimistic ? 'none' : 'flex' }}
        >
          <ReactionButton
            onReact={onToggleReaction}
            buttonComponent={() => (
              <IconButton
                variant="icon"
                aria-label="React to Comment"
                icon={<Icon size="md" faIcon={faSmilePlus} />}
              />
            )}
          />
          <Menu
            buttonProps={{
              'aria-label': 'Post Event Actions',
              icon: <Icon faIcon={faEllipsis} size="md" color="fg.muted" />,
            }}
            itemGroups={[menuItems]}
          />
        </Stack>
      </Stack>
    </>
  )
}
